/**
 * LanguageSelector — switches the application language.
 *
 * Lists every locale from `@tonnie/locale` and drives the change through
 * `useLocale().setLocale` (persists to localStorage + backend post-auth).
 * Native names, standard for a picker.
 *
 * Two trigger styles:
 *   • default (icon-only) — ghost globe button; used in the dashboard header.
 *   • `showLabel` — globe + current language's native name + chevron; used on the
 *     pre-auth pages (login/signup), where this is the ONLY way to pick a language.
 */
import { Check, ChevronDown, Globe } from 'lucide-react';
import { useLocale, useTranslation, SUPPORTED_LOCALES, LOCALE_NAMES } from '@tonnie/locale';
import { CountryFlag } from '@tonnie/ui';

import { Button } from '@/components/ui/button';
import { LOCALE_FLAG_COUNTRY } from '@/shared/locale-flags';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';

export interface ILanguageSelectorProps {
  className?: string;
  /** Show the current language name + chevron next to the globe (default: icon-only). */
  showLabel?: boolean;
}

export function LanguageSelector({ className, showLabel = false }: ILanguageSelectorProps) {
  const { locale, setLocale } = useLocale();
  const { t } = useTranslation();
  const ariaLabel = t('common.selectLanguage');
  const current = LOCALE_NAMES[locale]?.native;
  // The labelled trigger SHOWS the current language: its name starts with it
  // (WCAG 2.5.3 — a voice-control user says what they see).
  const labelledName = current ? `${current}, ${ariaLabel}` : ariaLabel;

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        {showLabel ? (
          <Button
            variant="ghost"
            size="sm"
            className={`gap-2 ${className ?? ''}`}
            aria-label={labelledName}
          >
            <Globe className="size-4" />
            <span className="text-sm font-medium">{current ?? ariaLabel}</span>
            <ChevronDown className="size-4 opacity-60" />
          </Button>
        ) : (
          <Button
            variant="ghost"
            size="icon"
            className={`relative ${className ?? ''}`}
            aria-label={ariaLabel}
          >
            <Globe className="size-5" />
            <span className="sr-only">{LOCALE_NAMES[locale]?.native ?? ariaLabel}</span>
          </Button>
        )}
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="min-w-48">
        {SUPPORTED_LOCALES.map((code) => (
          <DropdownMenuItem
            key={code}
            onClick={() => {
              if (locale !== code) void setLocale(code);
            }}
            className="flex items-center gap-2 cursor-pointer whitespace-nowrap"
          >
            <CountryFlag code={LOCALE_FLAG_COUNTRY[code]} size="md" />
            <span className="flex-1">{LOCALE_NAMES[code].native}</span>
            {locale === code && <Check className="w-4 h-4 text-primary-accessible" />}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

export default LanguageSelector;
